import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import { action, observable, computed } from 'mobx';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import { top } from '../utils/customPropStyles';
import Translate from './Translate';
import ClickLayer from './ClickLayer';
import Icon from './Icon';

const DropdownWrapper = styled<any, any>('div')`
    ${propStyles};
`;

DropdownWrapper.defaultProps = {
    flex: '1 0 100%',
    paddingY: 'sm',
    bgColor: 'white',
};

const DropdownInnerWrapper = styled<any, any>('div')`
    ${propStyles};
`;

DropdownInnerWrapper.defaultProps = {
    position: 'relative',
    display: 'flex',
};

const DropdownButton = styled<any, any>('button')`
    ${propStyles};
    outline: none;
    cursor: pointer;
`;

DropdownButton.defaultProps = {
    display: 'flex',
    flex: '1 1 auto',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    overflow: 'hidden',
    padding: 'sm',
    fontSize: 'sm',
    fontFamily: 'body',
    fontWeight: 'normal',
    color: 'body',
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    hover: {
        borderColor: 'gray500',
    },
    selected: {
        color: 'body',
        borderColor: 'gray500',
    },
};

const DropdownText = styled<any, any>('div')`
    ${propStyles};
    white-space: nowrap;
`;

DropdownText.defaultProps = {
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: 'inherit',
};

const DropdownMenu = styled<any, any>('div')`
    ${propStyles};
    ${top};
`;

DropdownMenu.defaultProps = {
    position: 'absolute',
    top: 'calc(100% + 1px)',
    zIndex: 'dropdown',
    width: ['100%', '80%'],
    margin: ['0', '0 10%'],
    bgColor: 'white',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: 'solid',
    borderRadius: 'sm',
    boxShadow: 'sm',
};

const DropdownList = styled<any, any>('ol')`
    ${propStyles};
    list-style: none;
    -ms-overflow-style: -ms-autohiding-scrollbar;
`;

DropdownList.defaultProps = {
    maxHeight: '300px',
    overflowX: 'hidden',
    overflowY: 'auto',
};

const DropdownListItem = styled<any, any>('li')`
    ${propStyles};
`;

DropdownListItem.defaultProps = {
    fontFamily: 'body',
    fontWeight: 'normal',
    paddingY: 'sm',
    paddingX: 'md',
    borderWidth: '1px',
    borderStyle: 'none none solid none',
    borderColor: 'gray300',
    cursor: 'pointer',
    alternated: {
        bgColor: 'gray100',
    },
    hover: {
        bgColor: 'gray100',
    },
    selected: {
        color: 'primary',
        fontWeight: 'bold',
    },
};

interface Props {
    theme?: { [property: string]: string | number };
    selectItemFn: (item: any) => void;
    selectedItem?: any;
    items: Array<{ id: string; label: string }>;
    placeHolder?: string;
    chevronIcon?: string;
    chevronIconExpanded?: string;
}

@observer
class Dropdown extends Component<Props> {
    @observable isOpen = false;
    @observable selectedItemIndex = -1;

    @action
    toggleOpen = () => {
        this.isOpen = !this.isOpen;
    };

    @computed
    get selectedItem() {
        return (
            this.props.selectedItem || this.props.items[this.selectedItemIndex]
        );
    }

    @action
    setSelectedItem = (item, index) => {
        this.selectedItemIndex = index;
        this.isOpen = false;
        this.props.selectItemFn(item);
    };

    render() {
        const {
            props: {
                theme,
                selectItemFn: selectItem,
                items,
                placeHolder,
                chevronIcon,
                chevronIconExpanded,
                ...props
            },
            isOpen,
            selectedItemIndex,
            toggleOpen,
            selectedItem,
            setSelectedItem,
        } = this;

        return (
            <DropdownWrapper {...props} {...theme.DropdownWrapper}>
                <DropdownInnerWrapper {...theme.DropdownInnerWrapper}>
                    <DropdownButton
                        onClick={toggleOpen}
                        data-selected={isOpen}
                        {...theme.DropdownButton}
                    >
                        <DropdownText {...theme.DropdownText}>
                            <Translate>
                                {selectedItem
                                    ? selectedItem.label
                                    : this.props.placeHolder}
                            </Translate>
                        </DropdownText>
                        <Icon
                            fileName={
                                isOpen
                                    ? chevronIconExpanded ||
                                      'DropdownChevronIconExpanded'
                                    : chevronIcon || 'DropdownChevronIcon'
                            }
                            {...theme.DropdownChevron}
                        />
                    </DropdownButton>
                    {isOpen && (
                        <Fragment>
                            <ClickLayer onClick={toggleOpen} />
                            <DropdownMenu {...theme.DropdownMenu}>
                                <DropdownList {...theme.DropdownList}>
                                    {items.map((item, i) => (
                                        <DropdownListItem
                                            key={i}
                                            onClick={() =>
                                                setSelectedItem(item, i)
                                            }
                                            data-alternated={
                                                i % 2 ? true : false
                                            }
                                            data-selected={
                                                i === selectedItemIndex
                                            }
                                            {...theme.DropdownListItem}
                                        >
                                            <Translate>{item.label}</Translate>
                                        </DropdownListItem>
                                    ))}
                                </DropdownList>
                            </DropdownMenu>
                        </Fragment>
                    )}
                </DropdownInnerWrapper>
            </DropdownWrapper>
        );
    }
}

export default withTheme(Dropdown);



// WEBPACK FOOTER //
// ./src/Components/Dropdown.tsx